import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { getToken } from '../../services/tokenService';
import { PreuveVirementDepot } from '../../components/documents/PreuveVirementDepot';
import { toast } from 'sonner';
import { CreditCard, Download, CheckCircle, AlertCircle, Loader2, ArrowLeft, ArrowRight, Building2, Shield, Hash, User, Mail, Phone, Banknote, Lock, Wallet, FileCheck2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { HighTechCard } from './shared';
import SepaMandateBlock from './SepaMandateBlock';
import { EcheancierDevis } from './EcheancierDevis';
import { useAuth } from '../../contexts/AuthContext';
import { API } from './constants';

// Seuil métier : paiement échelonné réservé aux contrats > 2500 €
// (donc actif à partir de 2501 €). À 2500 € pile → SEPA désactivé.
const SEUIL_ECHELONNEMENT_EUR = 2500;

/**
 * V36.94 — Stepper visuel du paiement échelonné.
 * Affiche les 3 étapes : Acompte 60 % → Prélèvement SEPA → Validation.
 * Le statut est dérivé de `acompteVirementConfirme` et `sepaInitiated`.
 */
function PaiementEchelonneStepper({ acompteConfirme, sepaInitiated, pctAcompte, nbMens }) {
  const steps = [
    {
      key: 'acompte',
      label: `Acompte ${pctAcompte} %`,
      hint: 'Virement bancaire',
      Icon: Wallet,
      status: acompteConfirme ? 'done' : 'current',
    },
    {
      key: 'sepa',
      label: 'Prélèvement SEPA',
      hint: `${100 - pctAcompte} % en ${nbMens} mensualité${nbMens > 1 ? 's' : ''}`,
      Icon: Banknote,
      status: !acompteConfirme ? 'upcoming' : sepaInitiated ? 'done' : 'current',
    },
    {
      key: 'validation',
      label: 'Validation',
      hint: 'Documents signés',
      Icon: FileCheck2,
      status: sepaInitiated ? 'current' : 'upcoming',
    },
  ];
  const statusStyles = {
    done: {
      circle: 'bg-emerald-500 text-white ring-emerald-200 shadow-emerald-300/50',
      label: 'text-emerald-700',
      hint: 'text-emerald-600/80',
      bar: 'bg-emerald-400',
    },
    current: {
      circle: 'bg-[#DEA900] text-[#0a3d62] ring-[#DEA900]/30 shadow-[#DEA900]/40 ring-4 animate-[pulse_2.4s_ease-in-out_infinite]',
      label: 'text-[#0a3d62]',
      hint: 'text-[#b88600]',
      bar: 'bg-[#DEA900]',
    },
    upcoming: {
      circle: 'bg-white text-slate-400 ring-slate-200 border-2 border-slate-200',
      label: 'text-slate-500',
      hint: 'text-slate-400',
      bar: 'bg-slate-200',
    },
  };
  return (
    <div
      className="relative rounded-2xl border border-[#DEA900]/30 bg-gradient-to-br from-[#0a3d62] via-[#0c4a78] to-[#0a3d62] p-5 shadow-lg overflow-hidden"
      data-testid="paiement-echelonne-stepper"
    >
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23DEA900'/%3E%3C/svg%3E\")",
        }}
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#f5cc3e] font-bold">
            Paiement échelonné · Progression
          </p>
          <span className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
            {steps.filter((s) => s.status === 'done').length}/3 étapes
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4">
          {steps.map((s, i) => {
            const style = statusStyles[s.status];
            const nextStyle = i < steps.length - 1 ? statusStyles[steps[i + 1].status] : null;
            const barColor = s.status === 'done' ? statusStyles.done.bar : statusStyles.upcoming.bar;
            return (
              <div
                key={s.key}
                className="relative flex flex-col items-center"
                data-testid={`stepper-step-${s.key}`}
                data-status={s.status}
              >
                {/* Connecteur (barre vers la droite) */}
                {i < steps.length - 1 && (
                  <div
                    className={`absolute top-5 left-1/2 right-0 -translate-x-0 h-[3px] rounded ${barColor} ${nextStyle?.bar === statusStyles.done.bar ? 'bg-emerald-400' : ''}`}
                    style={{ width: 'calc(100% - 2.5rem)', marginLeft: '2.5rem' }}
                  />
                )}
                <div
                  className={`relative z-10 h-11 w-11 rounded-full flex items-center justify-center ring-2 shadow-md transition-all duration-300 ${style.circle}`}
                >
                  {s.status === 'done' ? (
                    <CheckCircle className="h-6 w-6" />
                  ) : (
                    <s.Icon className="h-5 w-5" />
                  )}
                </div>
                <p className={`mt-2 text-[12px] font-bold leading-tight text-center ${style.label === 'text-slate-500' ? 'text-white/80' : 'text-white'}`}>
                  {s.label}
                </p>
                <p className={`text-[10px] leading-tight text-center mt-0.5 ${s.status === 'current' ? 'text-[#f5cc3e]' : 'text-white/50'}`}>
                  {s.hint}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * V36.94 — Bouton FTPE avec réactivité optimisée :
 * - feedback visuel immédiat dès le mouseDown (state local synchrone)
 * - micro-animation active:scale + ring focus
 * - bloquage anti-double-clic au-delà du `disabled` (debounce 600 ms)
 */
/**
 * V37.03 — Bouton FTPE simplifié et défensif.
 * - Plus de `lockRef`, plus d'`onMouseDown` synchrone (sources du faux loader).
 * - Loader visible UNIQUEMENT quand `ftpeLoading=true` (vraie action en cours).
 * - Double protection axios timeout 25s (cf. handleFtpePaymentVirement V37.00).
 */
function FtpeVirementButton({ loading = false, ftpeLoading = false, onClick }) {
  const isLoadingTexte = !!ftpeLoading;          // → texte « Enregistrement en cours… »
  const isDisabled = isLoadingTexte || !!loading; // → désactivé pendant création souscription
  return (
    <Button
      type="button"
      onClick={() => { if (!isDisabled) onClick?.(); }}
      disabled={isDisabled}
      className="w-full h-11 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-md hover:shadow-lg active:scale-[0.98] transition-all duration-150 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-400"
      data-testid="ftpe-pay-virement-btn"
    >
      {isLoadingTexte ? (
        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enregistrement en cours…</>
      ) : (
        <><CreditCard className="mr-2 h-4 w-4" />J&apos;ai effectué le virement FTPE</>
      )}
    </Button>
  );
}

export default function StepPaiement({ ctx }) {
  const {
    loading, montantTTC, souscriptionCreee, setEtape,
    ftpePaid, ftpeReferenceVirement, handleFtpePaymentVirement, isFtpeMember,
    ftpeLoading,
    formData,
    // V36.52 — state lift en parent pour transmettre à StepConfirmation
    paiementEchelonne: paiementEchelonneProp,
    setPaiementEchelonne: setPaiementEchelonneProp,
    // V36.74 — Échéancier personnalisé Super Admin (provenant de la proposition)
    echelonnementOverride,
  } = ctx;

  const { user } = useAuth() || {};
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';

  // Si le membre est déjà adhérent FTPE, pas besoin de payer FTPE
  const skipFtpe = isFtpeMember === true;
  // Mode paiement échelonné (60% virement + 40% SEPA en 3 mensualités)
  // V36.52 — Si parent gère l'état (workflow réel), on l'utilise. Sinon fallback local (tests / standalone).
  const [paiementEchelonneLocal, setPaiementEchelonneLocal] = useState(false);
  const paiementEchelonne = setPaiementEchelonneProp ? paiementEchelonneProp : paiementEchelonneLocal;
  const setPaiementEchelonne = setPaiementEchelonneProp || setPaiementEchelonneLocal;
  // V35.42 — Override du split par le super admin (depuis SepaMandateBlock)
  const [customSplit, setCustomSplit] = useState(null);
  // V36.94 — Acompte virement 60% confirmé par l'utilisateur (paiement échelonné).
  // Avant confirmation : le bouton "Mettre en place le prélèvement SEPA" est désactivé.
  // Après confirmation : il devient actif et la zone SEPA est mise en avant.
  const [acompteVirementConfirme, setAcompteVirementConfirme] = useState(false);
  const sepaBlockRef = useRef(null);
  const [nbPreuves, setNbPreuves] = useState(0);
  const [preuveObligatoire, setPreuveObligatoire] = useState(false);
  const [declaration, setDeclaration] = useState(false);
  useEffect(() => {
    axios.get(`${API}/preuves-virement/reglage`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(({ data }) => setPreuveObligatoire(!!data.obligatoire)).catch(() => {});
  }, []);
  const declarerVirement = async (mode, montant) => {
    if (!souscriptionCreee?.id) return true;
    setDeclaration(true);
    try {
      const { data } = await axios.post(`${API}/souscriptions/${souscriptionCreee.id}/virement-declare`,
        { mode, montant: Math.round((montant || 0) * 100) / 100 || 0.01, ftpe: !!(ftpePaid || isFtpeMember) },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement de la déclaration impossible');
      return null;
    } finally {
      setDeclaration(false);
    }
  };
  const refSolidaire = souscriptionCreee?.numero_police || souscriptionCreee?.id || 'REF';
  const refRCDS = `RCDS-${refSolidaire}`;
  const pctAcompte = customSplit && montantTTC
    ? Math.round((customSplit.acompte / montantTTC) * 100)
    : echelonnementOverride?.acompte_pct != null ? Math.round(Number(echelonnementOverride.acompte_pct) * 100) : 60;
  const pctSepa = 100 - pctAcompte;
  const nbMens = Number(echelonnementOverride?.nb_echeances) || 3;
  const montant60Defaut = (montantTTC || 0) * (pctAcompte / 100);
  const montant60 = customSplit ? customSplit.acompte : montant60Defaut;
  const montantVirementImmediat = paiementEchelonne ? montant60 : (montantTTC || 0);
  const montantSepaActif = customSplit ? customSplit.sepa : (montantTTC || 0) * (pctSepa / 100);

  // Seuil : actif si montant > 2500€ (strict, donc à partir de 2501€),
  // OU si super admin (override systématique).
  const echelonnementDisponible = isSuperAdmin || (montantTTC || 0) > SEUIL_ECHELONNEMENT_EUR;
  const montantManquant = (SEUIL_ECHELONNEMENT_EUR + 1) - (montantTTC || 0);

  return (
    <HighTechCard gradient="green">
      <div className="p-1 space-y-4">
        {/* Section 1: Paiement FTPE — uniquement si pas déjà adhérent */}
        {!skipFtpe && (
        <div className="rounded-xl border-2 border-blue-300 bg-blue-50" data-testid="ftpe-payment-section">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-white/20 backdrop-blur"><span className="text-white font-bold">1</span></div>
                <div><h3 className="text-lg font-bold text-white">Paiement cotisation FTPE</h3><p className="text-blue-100 text-xs">Virement obligatoire avant le paiement RCDS</p></div>
              </div>
              {ftpePaid && <div className="flex items-center gap-1 bg-green-500/20 px-3 py-1 rounded-full"><CheckCircle className="h-4 w-4 text-green-300" /><span className="text-green-300 text-sm">Enregistre</span></div>}
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div className="p-4 rounded-xl bg-white text-center border border-blue-200"><p className="text-xs text-slate-500 mb-1">Cotisation annuelle FTPE</p><p className="text-3xl font-bold text-blue-700">200,00 &euro;</p></div>
            <div className="p-4 rounded-xl bg-white border border-blue-200 space-y-2">
              <h4 className="font-bold text-blue-800 text-sm flex items-center gap-2"><CreditCard className="h-4 w-4" /> RIB FTPE</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between py-1.5 border-b border-slate-100"><span className="text-slate-500">IBAN</span><span className="font-mono font-bold text-xs">FR76 1010 7004 7300 6370 6380 902</span></div>
                <div className="flex justify-between py-1.5 border-b border-slate-100"><span className="text-slate-500">BIC</span><span className="font-mono font-bold">BREDFRPPXXX</span></div>
                <div className="flex justify-between py-1.5 border-b border-slate-100"><span className="text-slate-500">Titulaire</span><span className="font-bold text-xs text-right">FED. TRES PETITES ENTREPRISES</span></div>
                <div className="flex justify-between py-1.5"><span className="text-slate-500">Banque</span><span className="font-bold text-xs">BRED BANQUE POPULAIRE</span></div>
              </div>
              <Button onClick={() => window.open(`${API}/static/RIB_FTPE_971.pdf`, '_blank')} variant="outline" size="sm" className="w-full border-blue-300 text-blue-700 hover:bg-blue-100 mt-2" data-testid="ftpe-rib-download">
                <Download className="mr-2 h-3 w-3" />Telecharger le RIB FTPE (PDF)
              </Button>
            </div>
            {ftpeReferenceVirement ? (
              <div className="space-y-2">
                <div className="p-3 rounded-lg bg-blue-100 border border-blue-300"><p className="text-blue-800 font-medium text-sm mb-1">Reference virement FTPE</p><div className="p-2 rounded bg-white font-mono text-center text-lg font-bold text-blue-700">{ftpeReferenceVirement}</div></div>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-300"><CheckCircle className="h-4 w-4 text-green-600" /><span className="text-green-700 font-medium text-sm">Paiement FTPE enregistre</span></div>
              </div>
            ) : (
              <FtpeVirementButton
                loading={loading}
                ftpeLoading={ftpeLoading}
                onClick={handleFtpePaymentVirement}
              />
            )}
          </div>
        </div>
        )}

        {/* Coordonnees du membre */}
        {formData?.raison_sociale && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="paiement-coordonnees-membre">
            <h4 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2"><Building2 className="h-4 w-4 text-blue-600" />Membre souscripteur</h4>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="flex items-center gap-1.5"><Building2 className="h-3 w-3 text-slate-400" /><span className="font-semibold">{formData.raison_sociale}</span></div>
              {formData.siret && <div className="flex items-center gap-1.5"><Shield className="h-3 w-3 text-slate-400" /><span className="font-mono">{formData.siret}</span></div>}
              {formData.code_naf && <div className="flex items-center gap-1.5"><Hash className="h-3 w-3 text-slate-400" />APE : {formData.code_naf}</div>}
              {(formData.nom_gerant || formData.prenom_gerant) && <div className="flex items-center gap-1.5"><User className="h-3 w-3 text-slate-400" />{formData.prenom_gerant} {formData.nom_gerant}</div>}
              {formData.email && <div className="flex items-center gap-1.5"><Mail className="h-3 w-3 text-slate-400" />{formData.email}</div>}
              {formData.telephone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-slate-400" />{formData.telephone}</div>}
            </div>
          </div>
        )}

        {/* Section 2: Paiement RCDS */}
        <div className={`rounded-xl ${!skipFtpe && !ftpePaid ? 'opacity-50 pointer-events-none' : ''}`} data-testid="rcds-payment-section">
          <div className="bg-gradient-to-r from-emerald-500 to-green-500 rounded-t-xl p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-white/20 backdrop-blur"><span className="text-white font-bold text-lg">{skipFtpe ? 1 : 2}</span></div>
              <div><h2 className="text-xl font-bold text-white">Paiement souscription RCDS BATICO</h2><p className="text-green-100 text-sm">{skipFtpe || ftpePaid ? 'Virement a OBJECTIF SCOP OUTREMER' : 'Effectuez d\'abord le paiement FTPE'}</p></div>
            </div>
          </div>
          {!skipFtpe && !ftpePaid && (
            <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-lg mt-4">
              <div className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-amber-600" /><p className="text-amber-700 text-sm">Effectuez d&apos;abord le paiement FTPE avant le paiement RCDS.</p></div>
            </div>
          )}
          <div className="p-6 space-y-6 bg-white rounded-b-xl">
            <div className="p-6 rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 text-center">
              <p className="text-sm text-slate-400 uppercase mb-2">
                {paiementEchelonne ? `Acompte virement (${montantTTC > 0 ? ((montant60 / montantTTC) * 100).toFixed(1) : '60'}%) — Solde ${montantTTC > 0 ? ((montantSepaActif / montantTTC) * 100).toFixed(1) : '40'}% en 4 prélèvements SEPA` : 'Montant Global - Contribution solidaire proposée'}
              </p>
              <p className="text-5xl font-black bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent" data-testid="rcds-montant-global">{montantVirementImmediat?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} &euro;</p>
              {paiementEchelonne && (
                <p className="text-xs text-emerald-300 mt-2">sur un total de {montantTTC?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  {customSplit && <span className="ml-2 text-amber-300">· ✱ Split personnalisé</span>}
                </p>
              )}
            </div>

            {/* Choix mode paiement : intégral vs échelonné */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rcds-mode-paiement">
              <button
                type="button"
                onClick={() => setPaiementEchelonne(false)}
                className={`text-left p-4 rounded-xl border-2 transition-all ${!paiementEchelonne ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                data-testid="rcds-mode-integral"
              >
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className={`h-5 w-5 ${!paiementEchelonne ? 'text-emerald-600' : 'text-slate-400'}`} />
                  <span className={`font-bold text-sm ${!paiementEchelonne ? 'text-emerald-700' : 'text-slate-700'}`}>Paiement intégral</span>
                  {!paiementEchelonne && <CheckCircle className="h-4 w-4 text-emerald-600 ml-auto" />}
                </div>
                <p className="text-xs text-slate-600">Virement bancaire en 1 fois — documents définitifs dès validation du dossier</p>
              </button>
              <button
                type="button"
                onClick={() => echelonnementDisponible && setPaiementEchelonne(true)}
                disabled={!echelonnementDisponible}
                title={!echelonnementDisponible ? `Disponible à partir de ${(SEUIL_ECHELONNEMENT_EUR + 1).toLocaleString('fr-FR')} €` : undefined}
                className={`text-left p-4 rounded-xl border-2 transition-all relative ${paiementEchelonne ? 'border-[#DEA900] bg-[#DEA900]/8 ring-2 ring-[#DEA900]/20' : echelonnementDisponible ? 'border-slate-200 bg-white hover:border-slate-300' : 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'}`}
                data-testid="rcds-mode-echelonne"
              >
                <div className="flex items-center gap-2 mb-1">
                  {!echelonnementDisponible ? (
                    <Lock className="h-5 w-5 text-slate-400" />
                  ) : (
                    <Banknote className={`h-5 w-5 ${paiementEchelonne ? 'text-[#b88600]' : 'text-slate-400'}`} />
                  )}
                  <span className={`font-bold text-sm ${paiementEchelonne ? 'text-[#b88600]' : 'text-slate-700'}`}>Paiement échelonné</span>
                  {paiementEchelonne && <CheckCircle className="h-4 w-4 text-[#b88600] ml-auto" />}
                  {isSuperAdmin && !echelonnementDisponible && (
                    <span className="ml-auto text-[10px] uppercase tracking-wider bg-[#0a3d62] text-white px-2 py-0.5 rounded">Override admin</span>
                  )}
                </div>
                <p className="text-xs text-slate-600">{pctAcompte} % par virement bancaire + {pctSepa} % en {nbMens} prélèvement{nbMens > 1 ? 's' : ''} SEPA Stripe mensuel{nbMens > 1 ? 's' : ''}</p>
                {!echelonnementDisponible && (
                  <p className="text-[11px] text-amber-700 font-semibold mt-2" data-testid="rcds-echelonne-seuil-info">
                    Disponible à partir de {(SEUIL_ECHELONNEMENT_EUR + 1).toLocaleString('fr-FR')} € de contribution
                    {montantManquant > 0 && ` — il manque ${montantManquant.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
                  </p>
                )}
                {isSuperAdmin && (
                  <p className="text-[10px] text-[#0a3d62] font-semibold mt-1 italic">Accès Super Admin : toujours actif</p>
                )}
              </button>
            </div>

            <div className="p-5 rounded-xl border-2 border-slate-200">
              <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><CreditCard className="h-5 w-5 text-green-600" />Coordonnees bancaires - OBJECTIF SCOP OUTREMER</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b"><span className="text-slate-500">Beneficiaire</span><span className="font-bold">OBJECTIF SCOP OUTREMER</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-slate-500">IBAN</span><span className="font-mono font-bold">FR76 1695 8000 0182 4755 2649 230</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-slate-500">BIC</span><span className="font-mono font-bold">QNTOFRP1XXX</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-slate-500">Banque</span><span className="font-bold">Qonto</span></div>
                <div className="flex justify-between py-2"><span className="text-slate-500">Ref. Solidaire</span><span className="font-mono font-bold text-green-600 bg-green-100 px-3 py-1 rounded" data-testid="rcds-ref-solidaire">{refRCDS}</span></div>
              </div>
              {paiementEchelonne && (
                <p className="text-[11px] text-slate-500 mt-3 italic">
                  Note : les {pctAcompte} % d&apos;acompte sont versés sur ce RIB Qonto. Les {pctSepa} % suivants seront prélevés par mandat SEPA Stripe.
                </p>
              )}
            </div>

            {/* Si paiement échelonné : afficher le stepper + bloc SEPA en dessous */}
            {paiementEchelonne && souscriptionCreee?.id && (
              <>
                <PaiementEchelonneStepper
                  acompteConfirme={acompteVirementConfirme}
                  sepaInitiated={false}
                  pctAcompte={pctAcompte}
                  nbMens={nbMens}
                />
                <EcheancierDevis souscriptionId={souscriptionCreee.id} />
                <div ref={sepaBlockRef}>
                  <SepaMandateBlock
                    souscriptionId={souscriptionCreee.id}
                    montantTotalTTC={montantTTC}
                    formData={formData}
                    referenceSolidaire={refSolidaire}
                    echelonnementOverride={echelonnementOverride}
                    acompteVirementConfirme={acompteVirementConfirme}
                    onSplitChange={(acompte, sepa) => {
                      // V35.42 : propagation du split admin → réajuste le montant à virer affiché en haut
                      const isCustom = Math.abs(sepa - (montantTTC || 0) * 0.4) > 0.01;
                      setCustomSplit(isCustom ? { acompte, sepa } : null);
                    }}
                  />
                </div>
              </>
            )}

            {souscriptionCreee?.id && (
              <PreuveVirementDepot souscriptionId={souscriptionCreee.id} onChange={setNbPreuves} obligatoire={preuveObligatoire} />
            )}

            <div className="flex justify-between pt-4">
              <Button onClick={() => setEtape(5)} variant="outline" className="h-12"><ArrowLeft className="mr-2 h-5 w-5" />Retour</Button>
              <Button
                onClick={async () => {
                  // V36.94 — Paiement échelonné : 1er clic confirme l'acompte 60%
                  // et active le bouton "Mettre en place le prélèvement SEPA" ;
                  // 2ème clic (après SEPA) avance à l'étape de confirmation.
                  if (paiementEchelonne && !acompteVirementConfirme) {
                    if (!(await declarerVirement('acompte', montant60))) return;
                    setAcompteVirementConfirme(true);
                    toast.success(`Acompte ${pctAcompte} % enregistré — vous pouvez maintenant mettre en place le prélèvement SEPA Stripe.`);
                    setTimeout(() => {
                      sepaBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 80);
                    return;
                  }
                  const res = paiementEchelonne ? { message: null } : await declarerVirement('integral', montantTTC);
                  if (!res) return;
                  toast.success('La souscription a bien été enregistrée', { description: "Dès validation de la souscription, les documents (attestations + CP/CG, reçus) seront disponibles dans votre ESPACE membre ou COOPER'S.", duration: 9000 });
                  setEtape(7);
                }}
                disabled={(!ftpePaid && !skipFtpe) || declaration || (preuveObligatoire && nbPreuves === 0)}
                className="h-12 px-8 bg-gradient-to-r from-emerald-500 to-green-500 disabled:opacity-50 active:scale-[0.98] transition-transform"
                data-testid="rcds-virement-effectue-btn"
              >
                {paiementEchelonne && !acompteVirementConfirme ? (
                  <>J&apos;ai effectué le virement (acompte {pctAcompte} %)<ArrowRight className="ml-2 h-5 w-5" /></>
                ) : (
                  <>J&apos;ai effectué le virement<ArrowRight className="ml-2 h-5 w-5" /></>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </HighTechCard>
  );
}
